<template>
  <div class="beauty-page" :style="pageStyle">
    <!-- 顶部导航栏 -->
    <van-nav-bar 
      :title="$t('choose.title')" 
      fixed
      safe-area-inset-top
      :style="navBarStyle"
    >
    </van-nav-bar>

    <!-- 内容区域 -->
    <div class="content-wrapper" :style="contentStyle">
      
      <!-- 地区筛选 -->
      <div class="filter-tabs">
        <div 
          class="filter-tab"
          :class="{ active: currentFilter === 'all' }"
          @click="changeFilter('all')"
        >
          {{ $t('common.all') }}
        </div>
        <div 
          v-for="area in areaList" 
          :key="area.id"
          class="filter-tab"
          :class="{ active: currentFilter === area.id }"
          @click="changeFilter(area.id)"
        >
          {{ area.name }}
        </div>
      </div>

      <!-- 列表内容 -->
      <div class="list-container">
        <van-pull-refresh 
          v-model="isRefreshing" 
          @refresh="onRefresh"
        >
          <!-- 网格视图 -->
          <div v-if="viewType === 1" class="grid-view">
            <div class="grid-column">
              <div 
                v-for="(item, index) in leftColumnList" 
                :key="`left_${item.id}_${index}`"
                class="grid-item"
                @click="goToProfile(item.id)"
              >
                <div class="simple-card">
                  <div class="card-image">
                    <img :src="item.image" :alt="item.class_name" />
                    <!-- 在线状态徽章 -->
                    <div class="online-badge" v-if="item.is_online">
                      <span class="online-dot"></span>
                      <span class="online-text">{{ $t('on line') }}</span>
                    </div>
                  </div>
                  <div class="card-content">
                    <div class="tags">
                      <span class="tag" v-if="item.tags_arr && item.tags_arr[0]">
                        {{ item.tags_arr[0] }}
                      </span>
                    </div>
                    <div class="card-footer">
                      <div class="user-info">
                        <div class="avatar-badge"></div>
                        <span class="username">{{ item.xuanfei_name }}</span>
                      </div>
                      <div class="popularity">
                        <van-icon name="like" size="12" color="#dc63a1" />
                        {{ item.renqi || 0 }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="grid-column">
              <div 
                v-for="(item, index) in rightColumnList" 
                :key="`right_${item.id}_${index}`"
                class="grid-item"
                @click="goToProfile(item.id)"
              >
                <div class="simple-card">
                  <div class="card-image">
                    <img :src="item.image" :alt="item.class_name" />
                    <!-- 在线状态徽章 -->
                    <div class="online-badge" v-if="item.is_online">
                      <span class="online-dot"></span>
                      <span class="online-text">{{ $t('on line') }}</span>
                    </div>
                  </div>
                  <div class="card-content">
                    <div class="tags">
                      <span class="tag" v-if="item.tags_arr && item.tags_arr[0]">
                        {{ item.tags_arr[0] }}
                      </span>
                    </div>
                    <div class="card-footer">
                      <div class="user-info">
                        <div class="avatar-badge"></div>
                        <span class="username">{{ item.xuanfei_name }}</span>
                      </div>
                      <div class="popularity">
                        <van-icon name="like" size="12" color="#dc63a1" />
                        {{ item.renqi || 0 }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 列表视图 -->
          <div v-else class="list-view">
            <div 
              v-for="(item, index) in beautyList" 
              :key="`list_${item.id}_${index}`"
              class="list-item"
              @click="goToProfile(item.id)"
            >
              <div class="simple-list-card">
                <div class="list-left">
                  <div class="image-container">
                    <img :src="item.image" :alt="item.class_name" />
                    <!-- 列表视图在线状态 -->
                    <div class="online-badge list-online-badge" v-if="item.is_online">
                      <span class="online-dot"></span>
                      <span class="online-text">{{ $t('on line') }}</span>
                    </div>
                  </div>
                </div>
                <div class="list-right">
                  <div class="list-header">
                    <div class="badges">
                      <span class="badge">
                        <van-icon name="vip" size="12" />
                        {{ $t('home.smrz') }}
                      </span>
                      <span class="badge">
                        <van-icon name="video" size="12" />
                        {{ $t('home.videorz') }}
                      </span>
                    </div>
                    <div class="location">{{ item.class_name }}</div>
                  </div>
                  <div class="list-tags">
                    <span 
                      v-for="(tag, tagIndex) in item.tags_arr" 
                      :key="tagIndex"
                      class="tag"
                      :class="`tag-${(tagIndex % 3) + 1}`"
                    >
                      {{ tag }}
                    </span>
                  </div>
                  <div class="list-info">
                    <div class="star-rating">
                      {{ $t('home.star') }}:
                      <span 
                        v-for="(star, starIndex) in item.star" 
                        :key="starIndex"
                        class="star"
                      ></span>
                    </div>
                    <div class="user-details">
                      {{ $t('home.sg') }}: {{ formatHeight(item.sg) }}
                      {{ $t('home.xw') }}: {{ item.sw }}
                      <template v-if="hasKnownValue(item.class_name)">
                        {{ $t('home.diqu') }}: {{ item.class_name }}
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 空状态 -->
          <van-empty
            v-if="!isLoading && beautyList.length === 0"
            :description="$t('common.noData')"
            class="empty-state"
          />

          <!-- 加载更多箭头 - 去掉文字 -->
          <div v-if="!isFinished && beautyList.length > 0" class="load-more-container">
            <div class="load-more-arrow" @click="loadMore">
              <div class="arrow-icon" :class="{ loading: isLoading }">
                <van-icon name="arrow-down" size="24" />
              </div>
            </div>
          </div>

          <!-- 没有更多数据提示 -->
          <div v-if="isFinished && beautyList.length > 0" class="no-more-container">
            <div class="no-more-text">
              {{ $t('common.nomore') }}
            </div>
          </div>
        </van-pull-refresh>
      </div>
    </div>

    <!-- 返回顶部按钮 -->
    <div class="back-to-top" :class="{ show: showBackTop }" @click="scrollToTop">
      <van-icon name="back-top" size="24" />
    </div>
  </div>
</template>

<script>
import { formatHeightFromCm } from '../../common/height';
import { Toast } from 'vant';

export default {
  name: 'BeautyPage',
  data() {
    return {
      viewType: 1,
      currentFilter: 'all',
      areaList: [],
      beautyList: [],
      currentPage: 1,
      totalCount: 0,
      pageSize: 10,
      isLoading: false,
      isRefreshing: false,
      isFinished: false,
      showBackTop: false,
      safeAreaInsetTop: '0px',
      // 模拟在线状态数据（实际应该从API获取）
      mockOnlineStatus: [true, false, true, true, false, true, false, true, true, false]
    };
  },
  computed: {
    // 网格视图左右分栏
    leftColumnList() {
      return this.beautyList.filter((_, index) => index % 2 === 0);
    },
    rightColumnList() {
      return this.beautyList.filter((_, index) => index % 2 === 1);
    },
    // 导航栏样式 - 动态计算安全区域
    navBarStyle() {
      return {
        paddingTop: this.safeAreaInsetTop,
        height: `calc(46px + ${this.safeAreaInsetTop})`
      };
    },
    // 内容区域样式 - 动态计算边距
    contentStyle() {
      return {
        marginTop: `calc(46px + ${this.safeAreaInsetTop})`,
        paddingTop: '12px'
      };
    },
    // 页面样式
    pageStyle() {
      return {
        paddingTop: this.safeAreaInsetTop
      };
    }
  },
  methods: {
    hasKnownValue(value) {
      const normalized = String(value == null ? '' : value).trim().toLowerCase();
      return Boolean(normalized) && ![
        '未知', 'unknown', 'n/a', 'na', 'null', 'undefined', '-', '--'
      ].includes(normalized);
    },
    formatHeight(value) {
      return formatHeightFromCm(value);
    },
    /**
     * 安全的获取滚动位置 - 使用 window
     */
    scrollTops() {
      return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    },

    /**
     * 处理滚动显示返回顶部按钮
     */
    handleScroll() {
      const scrollTop = this.scrollTops();
      this.showBackTop = scrollTop > 300;
    },

    /**
     * 返回顶部
     */
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    },

    /**
     * 获取安全区域高度
     */
    getSafeAreaInsetTop() {
      // 获取安全区域顶部间距
      const safeArea = getComputedStyle(document.documentElement).getPropertyValue('--sat') || 
                      getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-top') ||
                      'env(safe-area-inset-top)';
      return safeArea;
    },

    /**
     * 切换视图类型
     */
    switchView(type) {
      this.viewType = type;
    },

    /**
     * 切换地区筛选
     */
    changeFilter(filterId) {
      this.currentFilter = filterId;
      this.currentPage = 1;
      this.beautyList = [];
      this.isFinished = false;
      this.getBeautyList(true);
    },

    /**
     * 获取地区列表
     */
    async getAreaList() {
      try {
        const response = await this.$http({
          method: 'get',
          url: 'address_list'
        });
        const areaData = Array.isArray(response.data) ? response.data : [];
        const flatAreas = Array.isArray(areaData[0]) ? areaData[0] : areaData;
        this.areaList = flatAreas.map(area => ({
          ...area,
          name: String(area.name || '').split(',')[0]
        }));
      } catch (error) {
        console.error('获取地区列表失败:', error);
        Toast(this.$t('common.loadFailed'));
      }
    },

    /**
     * 获取美女列表 - 添加在线状态处理
     */
    async getBeautyList(reset = false) {
      if (this.isLoading) return;
      
      if (reset) {
        this.currentPage = 1;
        this.beautyList = [];
        this.isFinished = false;
      }

      this.isLoading = true;

      try {
        const response = await this.$http({
          method: 'get',
          url: 'xuanfeilist',
          data: {
            id: this.currentFilter,
            page: this.currentPage,
            limit: this.pageSize
          }
        });

        // This endpoint currently returns the list directly. Keep compatibility
        // with paginated deployments that wrap it in { data, total }.
        const payload = response.data;
        const newData = Array.isArray(payload)
          ? payload
          : (payload && Array.isArray(payload.data) ? payload.data : []);
        
        // 为数据添加在线状态（实际项目中应该由后端返回）
        const processedData = newData.map((item, index) => {
          const localizedFlag = String(item.flag || '').split('|')[0] || '';
          return {
            ...item,
            sg: formatHeightFromCm(item.sg || item.hight || item.height || ''),
            image: item.image || item.img_url,
            renqi: item.renqi || item.dianzan || 0,
            tags_arr: Array.isArray(item.tags_arr)
              ? item.tags_arr
              : localizedFlag.split(/[，,]/).filter(Boolean),
            is_online: typeof item.is_online === 'boolean'
              ? item.is_online
              : (this.mockOnlineStatus[index % this.mockOnlineStatus.length] || false)
          };
        });
        
        if (reset) {
          this.beautyList = processedData;
        } else {
          this.beautyList = [...this.beautyList, ...processedData];
        }

        this.totalCount = payload && !Array.isArray(payload) && Number(payload.total)
          ? Number(payload.total)
          : this.beautyList.length;
        this.currentPage++;

        // 检查是否已加载所有数据
        if (Array.isArray(payload) || this.beautyList.length >= this.totalCount || newData.length < this.pageSize) {
          this.isFinished = true;
        }

      } catch (error) {
        console.error('获取列表失败:', error);
        Toast(this.$t('common.loadFailed'));
      } finally {
        this.isLoading = false;
        this.isRefreshing = false;
      }
    },

    /**
     * 加载更多
     */
    async loadMore() {
      if (!this.isFinished && !this.isLoading) {
        await this.getBeautyList();
      }
    },

    /**
     * 下拉刷新
     */
    onRefresh() {
      this.getBeautyList(true);
      Toast(this.$t('common.refresh'));
    },

    /**
     * 跳转到详情页
     */
    goToProfile(id) {
      this.$router.push({ path: '/profile?id=' + id });
    }
  },
  async created() {
    await Promise.all([
      this.getAreaList(),
      this.getBeautyList(true)
    ]);
  },
  mounted() {
    // 设置安全区域
    this.safeAreaInsetTop = this.getSafeAreaInsetTop();
    
    // 使用 window 的滚动监听
    window.addEventListener('scroll', this.handleScroll);
    
    // 监听窗口变化
    window.addEventListener('resize', () => {
      this.safeAreaInsetTop = this.getSafeAreaInsetTop();
    });
  },
  beforeDestroy() {
    // 移除事件监听
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', () => {});
  }
};
</script>

<style lang="less" scoped>
.beauty-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  position: relative;
  /* 设置安全区域变量 */
  --safe-area-inset-top: env(safe-area-inset-top, 0px);
  --safe-area-inset-bottom: env(safe-area-inset-bottom, 0px);
}

/* 导航栏样式 - 修复定位问题 */
.van-nav-bar {
  background: linear-gradient(135deg, #3498db 0%, #8e44ad 100%);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  /* 动态高度由 computed 控制 */
  
  :deep(.van-nav-bar__title) {
    color: white;
    font-weight: 600;
    font-size: 40px;
  }
  
  :deep(.van-nav-bar__right) {
    .view-toggle {
      display: flex;
      align-items: center;
      gap: 12px;
      
      .van-icon {
        padding: 6px;
        border-radius: 8px;
        background: rgba(255, 255, 255, 0.2);
        transition: all 0.3s ease;
        
        &.active {
          background: rgba(255, 255, 255, 0.4);
          transform: scale(1.1);
        }
      }
    }
  }
}

/* 内容区域 - 修复边距问题 */
.content-wrapper {
  /* 动态边距由 computed 控制 */
  padding: 0 12px 80px;
  min-height: calc(100vh - 46px - var(--safe-area-inset-top, 0px));
}

/* 筛选标签 */
.filter-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 16px 0;
  padding: 0 4px;
}

.filter-tab {
  padding: 8px 16px;
  background: white;
  border-radius: 20px;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #e8e8e8;
  white-space: nowrap;
  
  &:hover {
    background: #f8f9fa;
    transform: translateY(-1px);
  }
  
  &.active {
    background: linear-gradient(135deg, #3498db 0%, #8e44ad 100%);
    color: white;
    border-color: transparent;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  }
}

/* 列表容器 */
.list-container {
  min-height: 400px;
}

/* 网格视图 */
.grid-view {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 8px 0;
}

.grid-column {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.grid-item {
  border-radius: 12px;
  overflow: hidden;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }
}

/* 简单卡片样式 */
.simple-card {
  width: 100%;
  
  .card-image {
    position: relative;
    width: 100%;
    aspect-ratio: 3/4;
    overflow: hidden;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  
  .card-content {
    padding: 12px;
    
    .tags {
      margin-bottom: 8px;
      
      .tag {
        background: #3498db;
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 28px;
      }
    }
    
    .card-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 22px;
      color: #666;
      
      .user-info {
        display: flex;
        align-items: center;
        gap: 6px;
        
        .avatar-badge {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #efba8f;
        }
      }
      
      .popularity {
        display: flex;
        align-items: center;
        gap: 4px;
      }
    }
  }
}

/* 绿色在线徽章样式 - 热门公告区域 */
.online-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.95);
  padding: 4px 8px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
  animation: onlinePulse 2s infinite;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(34, 197, 94, 0.2);
  z-index: 1000;
}

.online-dot {
  width: 8px;
  height: 8px;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  border-radius: 50%;
  margin-right: 4px;
  animation: dotPulse 1.5s infinite;
  box-shadow: 0 0 6px rgba(34, 197, 94, 0.6);
}

.online-text {
  font-size: 11px;
  font-weight: 600;
  color: #15803d;
  white-space: nowrap;
}

/* 列表视图在线徽章调整 */
.list-online-badge {
  top: 4px;
  right: 4px;
  padding: 3px 6px;
  
  .online-dot {
    width: 6px;
    height: 6px;
    margin-right: 3px;
  }
  
  .online-text {
    font-size: 10px;
  }
}

/* 在线徽章动画 */
@keyframes onlinePulse {
  0% {
    box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
  }
  50% {
    box-shadow: 0 2px 12px rgba(34, 197, 94, 0.5);
  }
  100% {
    box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
  }
}

@keyframes dotPulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 6px rgba(34, 197, 94, 0.6);
  }
  50% {
    transform: scale(1.1);
    box-shadow: 0 0 10px rgba(34, 197, 94, 0.8);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 6px rgba(34, 197, 94, 0.6);
  }
}

/* 列表视图 */
.list-view {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0;
}

.list-item {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }
}

/* 简单列表卡片样式 */
.simple-list-card {
  display: flex;
  gap: 12px;
  padding: 16px;
  
  .list-left {
    flex-shrink: 0;
    
    .image-container {
      position: relative;
      width: 80px;
      height: 80px;
      border-radius: 8px;
      overflow: hidden;
      
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  }
  
  .list-right {
    flex: 1;
    
    .list-header {
      margin-bottom: 8px;
      
      .badges {
        display: flex;
        gap: 6px;
        margin-bottom: 6px;
        
        .badge {
          background: linear-gradient(135deg, #a8edea, #fed6e3);
          color: #2d3748;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 24px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 4px;
        }
      }
      
      .location {
        font-size: 30px;
        font-weight: 600;
        color: #2d3748;
      }
    }
    
    .list-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 12px;
      
      .tag {
        padding: 4px 8px;
        border-radius: 12px;
        font-size: 20px;
        color: white;
        font-weight: 500;
        
        &.tag-1 {
          background: #fe257f;
        }
        &.tag-2 {
          background: #ff9702;
        }
        &.tag-3 {
          background: #0fa7fe;
        }
      }
    }
    
    .list-info {
      .star-rating {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 22px;
        color: #e53e3e;
        margin-bottom: 4px;
        
        .star {
          width: 12px;
          height: 12px;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="%23f6ad55" d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>') no-repeat;
        }
      }
      
      .user-details {
        font-size: 28px;
        color: #718096;
        line-height: 1.4;
      }
    }
  }
}

/* 空状态 */
.empty-state {
  padding: 60px 20px;
  
  :deep(.van-empty__description) {
    color: #a0aec0;
    font-size: 14px;
  }
}

/* 加载更多箭头 - 只有箭头没有文字 */
.load-more-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px 0;
  margin-top: 16px;
}

.load-more-arrow {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 16px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3498db 0%, #8e44ad 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
  width: 60px;
  height: 60px;
  
  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
  }
  
  &:active {
    transform: translateY(0) scale(1);
    box-shadow: 0 2px 10px rgba(102, 126, 234, 0.4);
  }
}

.arrow-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  
  &.loading {
    animation: rotate 1s linear infinite;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 没有更多数据提示 */
.no-more-container {
  text-align: center;
  padding: 32px 20px;
  color: #a0aec0;
}

.no-more-text {
  font-size: 14px;
}

/* 返回顶部按钮 */
.back-to-top {
  position: fixed;
  right: 20px;
  bottom: 80px;
  z-index: 999;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #3498db 0%, #8e44ad 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: 0;
  visibility: hidden;
  
  &.show {
    opacity: 1;
    visibility: visible;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(102, 126, 234, 0.6);
  }
}

/* 响应式设计 */
@media (max-width: 375px) {
  .content-wrapper {
    padding: 0 10px 70px;
  }
  
  .filter-tabs {
    gap: 6px;
  }
  
  .filter-tab {
    padding: 6px 12px;
    font-size: 13px;
  }
  
  .grid-view {
    gap: 10px;
  }
  
  .load-more-arrow {
    width: 56px;
    height: 56px;
    padding: 14px;
  }
  
  .simple-list-card {
    padding: 12px;
    
    .list-left .image-container {
      width: 70px;
      height: 70px;
    }
  }
  
  /* 在线徽章响应式调整 */
  .online-badge {
    padding: 3px 6px;
    
    .online-text {
      font-size: 10px;
    }
  }
  
  .list-online-badge {
    padding: 2px 4px;
    
    .online-text {
      font-size: 9px;
    }
  }
}

@media (min-width: 768px) {
  .content-wrapper {
    padding: 0 16px 80px;
  }
  
  .grid-view {
    gap: 16px;
  }
  
  .list-view {
    gap: 16px;
  }
}

/* 深色模式支持 */
@media (prefers-color-scheme: dark) {
  .beauty-page {
    background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
  }
  
  .filter-tab {
    background: #2d3748;
    color: #e2e8f0;
    border-color: #4a5568;
    
    &:hover {
      background: #4a5568;
    }
  }
  
  .grid-item,
  .list-item,
  .simple-card,
  .simple-list-card {
    background: #2d3748;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
  
  .simple-card .card-content .card-footer,
  .simple-list-card .list-right .list-header .location {
    color: #e2e8f0;
  }
  
  .simple-list-card .list-right .list-info .user-details {
    color: #a0aec0;
  }
  
  /* 深色模式在线徽章适配 */
  .online-badge {
    background: rgba(255, 255, 255, 0.1) !important;
    border-color: rgba(34, 197, 94, 0.4) !important;
    
    .online-text {
      color: #22c55e !important;
    }
  }
}

/* 无障碍访问支持 */
@media (prefers-reduced-motion: reduce) {
  .grid-item,
  .list-item,
  .filter-tab,
  .load-more-arrow,
  .back-to-top {
    transition: none;
  }
  
  .arrow-icon.loading {
    animation: none;
  }
  
  .online-badge,
  .online-dot {
    animation: none !important;
  }
}

/* 紧急修复：如果还是看不见，使用这个强制方案 */
.beauty-page.force-fix {
  padding-top: env(safe-area-inset-top, 0px);
}

.content-wrapper.force-fix {
  margin-top: 0 !important;
  padding-top: calc(46px + env(safe-area-inset-top, 0px)) !important;
}

/* 选妃列表：黑金轻奢主题 */
.beauty-page {
  color: #f3eadb;
  background:
    linear-gradient(rgba(3, 5, 8, 0.78), rgba(3, 5, 8, 0.94)),
    url('../../assets/img/luxury-dating-bg-v2.png') center top / cover fixed no-repeat !important;
}

.content-wrapper {
  padding-left: 14px;
  padding-right: 14px;
  background: transparent;
}

.filter-tabs {
  gap: 10px;
  margin: 18px 0 14px;
  padding: 12px;
  border: 1px solid rgba(218, 180, 105, 0.24);
  border-radius: 18px;
  background: rgba(7, 10, 14, 0.82);
  backdrop-filter: blur(12px);
}

.filter-tab {
  padding: 7px 16px;
  color: #c9bda8;
  font-size: 24px;
  border: 1px solid rgba(218, 180, 105, 0.35);
  border-radius: 999px;
  background: rgba(17, 19, 23, 0.92);
  box-shadow: none;

  &:hover {
    color: #efd38e;
    background: rgba(194, 143, 59, 0.14);
  }

  &.active {
    color: #171006;
    border-color: rgba(247, 220, 160, 0.7);
    background: linear-gradient(110deg, #aa7830, #e8c979, #bd873d);
    box-shadow: 0 7px 18px rgba(183, 128, 44, 0.25);
  }
}

.grid-view,
.grid-column {
  gap: 14px;
}

.grid-item,
.simple-card {
  color: #f3eadb;
  border-radius: 18px;
  background: rgba(7, 10, 14, 0.94) !important;
}

.grid-item {
  border: 1px solid rgba(218, 180, 105, 0.34);
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.04);

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(232, 199, 124, 0.62);
    box-shadow: 0 18px 38px rgba(0, 0, 0, 0.5);
  }
}

.simple-card {
  .card-image {
    background: #0c0e12;

    img {
      transition: transform 0.35s ease;
    }
  }

  &:hover .card-image img {
    transform: scale(1.025);
  }

  .card-content {
    padding: 12px 11px 13px;
    border-top: 1px solid rgba(218, 180, 105, 0.2);
    background: linear-gradient(145deg, rgba(16, 18, 23, 0.98), rgba(6, 8, 12, 0.98));

    .tags {
      margin-bottom: 9px;

      .tag {
        display: inline-block;
        padding: 4px 9px;
        color: #e9cb8f;
        font-size: 24px;
        line-height: 1.15;
        border: 1px solid rgba(218, 180, 105, 0.5);
        border-radius: 999px;
        background: rgba(199, 148, 63, 0.12);
      }
    }

    .card-footer {
      color: #d5c9b6;
      font-size: 21px;

      .avatar-badge {
        background: #d8ad60;
        box-shadow: 0 0 8px rgba(216, 173, 96, 0.5);
      }

      .popularity {
        color: #d8b772;
      }
    }
  }
}

.online-badge {
  color: #a9efbd;
  border-color: rgba(72, 198, 111, 0.35);
  background: rgba(7, 16, 11, 0.9);
  box-shadow: 0 5px 14px rgba(0, 0, 0, 0.28);
}

.online-text {
  color: #7cdb98;
}

.load-more-arrow,
.back-to-top {
  color: #171006;
  border: 1px solid rgba(247, 220, 160, 0.65);
  background: linear-gradient(110deg, #a97732, #e7c87c, #bb853c);
  box-shadow: 0 8px 22px rgba(185, 130, 48, 0.28);
}

.no-more-container,
.no-more-text {
  color: #aa9e8b;
}

/* 320-430px：紧凑但不挤压，兼容长地区名与多语言文案 */
@media (max-width: 430px) {
  .beauty-page {
    width: 100%;
    max-width: 100vw;
    min-height: 100dvh;
    padding-top: 0 !important;
    overflow-x: clip;
    background-attachment: scroll !important;
    box-sizing: border-box;
  }

  .beauty-page,
  .beauty-page * {
    box-sizing: border-box;
  }

  .content-wrapper {
    width: 100%;
    min-width: 0;
    min-height: calc(100dvh - 3.5rem);
    margin-top: calc(clamp(3rem, 14vw, 3.5rem) + env(safe-area-inset-top)) !important;
    padding: .75rem .75rem calc(5rem + env(safe-area-inset-bottom)) !important;
  }

  .filter-tabs {
    flex-wrap: nowrap;
    gap: .5rem;
    margin: 0 0 .75rem;
    padding: .65rem;
    overflow-x: auto;
    overflow-y: hidden;
    border-radius: 1rem;
    scroll-padding-inline: .65rem;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }

  .filter-tabs::-webkit-scrollbar {
    display: none;
  }

  .filter-tab {
    flex: 0 0 auto;
    max-width: min(75vw, 16rem);
    padding: .5rem .8rem;
    font-size: .9rem;
    line-height: 1.25;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .list-container,
  .grid-view,
  .grid-column,
  .grid-item,
  .simple-card,
  .card-content,
  .card-footer,
  .user-info,
  .list-view,
  .list-item,
  .simple-list-card,
  .list-right {
    min-width: 0;
  }

  .grid-view {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: .65rem;
    padding: 0;
  }

  .grid-column {
    gap: .65rem;
  }

  .grid-item,
  .simple-card {
    border-radius: 1rem;
  }

  .simple-card .card-content {
    padding: .7rem .65rem .75rem;
  }

  .simple-card .card-content .tags {
    min-height: 1.55rem;
    margin-bottom: .45rem;
  }

  .simple-card .card-content .tags .tag {
    display: inline-block;
    max-width: 100%;
    padding: .3rem .5rem;
    font-size: .78rem;
    line-height: 1.2;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .simple-card .card-content .card-footer {
    gap: .35rem;
    font-size: .78rem;
    line-height: 1.3;
  }

  .simple-card .card-footer .user-info {
    flex: 1 1 auto;
    gap: .35rem;
  }

  .simple-card .card-footer .username {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .simple-card .card-footer .popularity {
    flex: 0 0 auto;
    max-width: 42%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .online-badge {
    top: .4rem;
    right: .4rem;
    max-width: calc(100% - .8rem);
    padding: .25rem .4rem;
  }

  .online-badge .online-text {
    min-width: 0;
    font-size: .68rem;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .list-view {
    gap: .65rem;
    padding: 0;
  }

  .simple-list-card {
    align-items: flex-start;
    gap: .7rem;
    padding: .75rem;
  }

  .simple-list-card .list-left .image-container {
    width: clamp(4.5rem, 23vw, 5.5rem);
    height: auto;
    aspect-ratio: 1 / 1;
  }

  .simple-list-card .list-right .list-header {
    margin-bottom: .45rem;
  }

  .simple-list-card .list-right .list-header .badges {
    flex-wrap: wrap;
    gap: .35rem;
    margin-bottom: .4rem;
  }

  .simple-list-card .list-right .list-header .badges .badge {
    min-width: 0;
    max-width: 100%;
    padding: .3rem .45rem;
    font-size: .75rem;
    line-height: 1.2;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .simple-list-card .list-right .list-header .location {
    font-size: .95rem;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }

  .simple-list-card .list-right .list-tags {
    gap: .3rem;
    margin-bottom: .55rem;
  }

  .simple-list-card .list-right .list-tags .tag {
    max-width: 100%;
    padding: .3rem .45rem;
    font-size: .72rem;
    line-height: 1.2;
    overflow-wrap: anywhere;
  }

  .simple-list-card .list-right .list-info .star-rating {
    flex-wrap: wrap;
    gap: .2rem;
    font-size: .8rem;
  }

  .simple-list-card .list-right .list-info .user-details {
    font-size: .78rem;
    line-height: 1.45;
    overflow-wrap: anywhere;
  }

  .load-more-container {
    margin-top: .75rem;
    padding: 1rem 0;
  }

  .load-more-arrow {
    width: 3rem;
    height: 3rem;
    padding: .65rem;
  }

  .back-to-top {
    right: .75rem;
    bottom: calc(4.5rem + env(safe-area-inset-bottom));
    width: 2.75rem;
    height: 2.75rem;
  }

  .no-more-container {
    padding: 1.25rem .75rem;
  }
}

@media (max-width: 350px) {
  .content-wrapper {
    padding-right: .55rem !important;
    padding-left: .55rem !important;
  }

  .grid-view,
  .grid-column {
    gap: .5rem;
  }

  .simple-card .card-content {
    padding: .6rem .5rem .65rem;
  }

  .simple-card .card-content .card-footer {
    font-size: .72rem;
  }

  .simple-list-card {
    gap: .55rem;
    padding: .6rem;
  }

  .simple-list-card .list-left .image-container {
    width: 4.25rem;
  }
}

@supports not (overflow: clip) {
  @media (max-width: 430px) {
    .beauty-page {
      overflow-x: hidden;
    }
  }
}
</style>
